import { createI18n } from 'vue-i18n'
import itMessages from '@/locales/it.json'
import enMessages from '@/locales/en.json'
import apiClient from '@/api/client'

export type MessageSchema = typeof itMessages
export type AppLocale = 'it' | 'en'

export const SUPPORTED_LOCALES: AppLocale[] = ['it', 'en']
const LOCALE_STORAGE_KEY = 'app.locale'

// Bundled fallbacks per locale — the base each remote-message merge layers on.
const bundled: Record<AppLocale, MessageSchema> = {
  it: itMessages,
  en: enMessages,
}

/** The persisted locale (localStorage), defaulting to Italian. */
export function getSavedLocale(): AppLocale {
  const saved = localStorage.getItem(LOCALE_STORAGE_KEY)
  return saved === 'en' || saved === 'it' ? saved : 'it'
}

const i18n = createI18n({
  legacy: false,
  locale: getSavedLocale(),
  fallbackLocale: 'it',
  messages: {
    it: itMessages,
    en: enMessages,
  },
})

/**
 * Fetch translations from backend for `locale` and merge over the bundled
 * messages for that same locale. Falls back silently to the bundled file if the
 * API is unreachable (or has no entries for the locale).
 */
export async function loadRemoteMessages(locale: AppLocale = getSavedLocale()): Promise<void> {
  try {
    const { data } = await apiClient.get<Record<string, string>>(`/static-text/locale/${locale}`)
    i18n.global.setLocaleMessage(locale, {
      ...bundled[locale],
      ...data,
    })
  } catch {
    // Backend unreachable — keep bundled fallback
    console.warn(`[i18n] Could not load remote messages for "${locale}", using bundled fallback`)
  }
}

/**
 * Switch the active locale: persist the choice, apply it globally, and load
 * that locale's remote messages. Persisted in localStorage (no DB entity yet).
 */
export async function setLocale(locale: AppLocale): Promise<void> {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  i18n.global.locale.value = locale
  await loadRemoteMessages(locale)
}

export default i18n
